import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import Tabs from '../components/Tabs';
import { useHubExcelExport } from '../components/ExcelExportButton';
import { useRegisterPageExports } from '../exports/pageExports';
import { usePersistentValue } from '../hooks/usePersistentState';
import ForecastBalanceSheetTab from './ForecastBalanceSheetTab';
import ForecastCashflowTab from './ForecastCashflowTab';
import ForecastSocialTab from './ForecastSocialTab';
import PayrollMassPlanPage from './hr/PayrollMassPlanPage';
import { useForecastAssumptions } from '../hooks/useForecastAssumptions';
import VatYearPage from './financials/VatYearPage';

/* Prévisions de trésorerie — les SUPPORTS du plan de liquidité (réorganisation
   2026-08-19, recentrée le 2026-09-17) : ce qui CONSTRUIT le cash de
   l'exercice, à une adresse.

     • Créances clients / Dettes fournisseurs / Acomptes clients — postes du
       bilan prévisionnel (écoulements), ex-section « Exploitation ».
     • TVA / Charges sociales — décomptes de l'année (réel + prévisionnel).
     • Financement — dette et fonds propres : c'est le plan de liquidité qui
       révèle le besoin auquel cet onglet répond (ex-« Investissement &
       financement » ; le capex et les mouvements sont partis au Prévisionnel).

   **Le PLAN lui-même a quitté cette section le 2026-09-17** : il se LIT parmi
   les états financiers, en accès direct depuis le menu
   (`FinancialsReportsHub only={['cashplan']}`, même composant, un seul
   moteur). Ici on règle les hypothèses qui le nourrissent ; là on le lit.

   Chaque onglet monte la page existante en mode embedded — aucun moteur
   nouveau. La Simulation 12M (autre moteur) reste une section à part. */
/* Les CHARGES SOCIALES sont parties au Résultat prévisionnel le 2026-09-18 :
   c'est une charge qu'on planifie, et elle se lit à côté des salaires qui la
   produisent. La TVA reste ici : son décompte est un support du plan, pas une
   charge d'exploitation. */
/* LE CAPEX, LE BILAN ET LE FLUX arrivent du Résultat prévisionnel le
   2026-09-18 : cette section-là construit le RÉSULTAT ligne par ligne ; le
   bilan et le flux en DÉCOULENT, et le capex les nourrit tous les deux. Ils
   ferment donc la liste, après les supports qui les alimentent. */
/* CHARGES SOCIALES, DÉCOMPTE PERS. et MOUVEMENTS EXCEPTIONNELS arrivent du
   Résultat prévisionnel le 2026-09-18 : ce sont des DÉCAISSEMENTS à échéance,
   ils se lisent avec la TVA et les autres supports du plan, pas avec la
   construction du résultat ligne par ligne. */
/* L'ONGLET INVESTISSEMENTS EST PARTI (2026-09-19, soir) : il écrivait
   l'hypothèse `bs_capex` de l'exercice, que plus personne ne lit depuis que le
   bilan et le compte de résultat prennent leurs amortissements dans le PLAN
   D'INVESTISSEMENT. Le garder, c'était une saisie qui n'allait plus nulle part ;
   ce qui y avait été posé est repris dans le plan (migration debfb628c3f0).
   Les MOUVEMENTS EXCEPTIONNELS sont partis le même jour aux Prévisions, étape
   du sujet « Synthèse et financement » : une écriture ponctuelle est un
   mouvement de trésorerie qu'on décide, comme un emprunt. */
const TAB_IDS = ['clients', 'fourn', 'advances', 'vat', 'social', 'people',
                 'financing', 'bilan', 'cash'];

export default function TresorerieHub({ activeTenant, addToast, year, onYearChange }) {
  const { t } = useTranslation();
  const tid = activeTenant?.id;
  const [tab, setTab] = usePersistentValue(`tresorerie_tab_${tid}`, 'clients');
  /* Les hypothèses du bilan et du flux (délais, lignes de capex et de
     financement) arrivent avec eux : elles vivent en base par client × année,
     et c'est l'écran qui les consomme qui les charge. */
  const { a, setKey, reload: reloadAssum } = useForecastAssumptions(tid, year);
  const assumptions = {
    dso: a.dso, dpo: a.dpo, dio: a.dio, pac: a.pac,
    setDso: (v) => setKey('dso', v), setDpo: (v) => setKey('dpo', v),
    setDio: (v) => setKey('dio', v), setPac: (v) => setKey('pac', v),
  };
  const cashflow = {
    invLines: a.cf_inv, finLines: a.cf_fin,
    setInvLines: (u) => setKey('cf_inv', u), setFinLines: (u) => setKey('cf_fin', u),
  };
  // Unité partagée avec les autres supports Finance (même mémoire par client).
  const [unit, setUnit] = usePersistentValue(`fc_steps_unit_${tid}`, 'kchf');
  const active = TAB_IDS.includes(tab) ? tab : 'clients';

  const TABS = [
    { id: 'clients', label: t('finance:forecast.steps.pos-clients') },
    { id: 'fourn', label: t('finance:forecast.steps.pos-fourn') },
    { id: 'advances', label: t('finance:forecast.steps.pos-advances') },
    { id: 'vat', label: t('finance:forecast.steps.fc-vat') },
    { id: 'social', label: t('finance:forecast.steps.fc-social') },
    { id: 'people', label: t('finance:forecast.tabs.people') },
    { id: 'financing', label: t('finance:forecast.steps.fc-financing') },
    { id: 'bilan', label: t('finance:forecast.tabs.bilan') },
    { id: 'cash', label: t('finance:forecast.tabs.cash') },
  ];

  // Rendu d'un onglet — à l'écran (unité du hub) et pour l'export Excel (CHF forcé).
  const renderTab = (id, forExport = false) => {
    const u = forExport ? 'chf' : unit;
    switch (id) {
      case 'clients':   return <ForecastBalanceSheetTab focus="pos-clients" activeTenant={activeTenant} year={year} />;
      case 'fourn':     return <ForecastBalanceSheetTab focus="pos-fourn" activeTenant={activeTenant} year={year} />;
      case 'advances':  return <ForecastBalanceSheetTab focus="pos-advances" activeTenant={activeTenant} year={year} />;
      case 'vat':       return <VatYearPage activeTenant={activeTenant} year={year} unit={u} />;
      case 'social':    return <ForecastSocialTab activeTenant={activeTenant} year={year} unit={u} />;
      case 'people':    return <PayrollMassPlanPage embedded unit={u} activeTenant={activeTenant} year={year} onYearChange={onYearChange} />;
      case 'bilan':     return <ForecastBalanceSheetTab activeTenant={activeTenant} year={year} unit={u} onAssumptionsChanged={reloadAssum} />;
      case 'cash':      return <ForecastCashflowTab activeTenant={activeTenant} year={year} assumptions={assumptions} cashflow={cashflow} unit={u} />;
      case 'financing': return <ForecastBalanceSheetTab focus="fc-financing" activeTenant={activeTenant} year={year} />;
      default: return null;
    }
  };

  // Export Excel — le hub entier d'un coup (1 onglet = 1 feuille), servi par
  // l'icône télécharger de la barre supérieure (registre `pageExports`).
  const excel = useHubExcelExport({
    tabs: TABS.map((tb) => ({ label: tb.label, render: () => renderTab(tb.id, true) })),
    fileBase: t('nav:items.tresorerie-hub'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  const exportOptions = useMemo(
    () => [{ id: 'excel', label: 'Excel', run: excel.run }],
    [excel.run],
  );

  useRegisterPageExports(exportOptions);
  /* L'unité monte à la BANDE DE PAGE (2026-09-17) : elle vivait sur la ligne
     des onglets, où elle tenait une seconde ligne de commandes. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);

  return (
    <PageShell framed kind="report" unit={pageUnit} title={t('nav:items.tresorerie-hub')}>
      <Tabs tabs={TABS} active={active} onChange={setTab} />
      <div className="page-shell__panel">
        {renderTab(active)}
      </div>

      {excel.stage}
    </PageShell>
  );
}
